import { useEffect, useState, type FormEvent } from 'react';
import type { CertificateUploadContext } from '@mvcp/contracts';
import { getCertificateUploadContext, uploadCertificateForToken } from '../api';
import { MAX_TEXT_LEN } from '../lib/field-limits';

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; context: CertificateUploadContext }
  | { kind: 'invalid' };

/**
 * PUBLIC passwordless certificate-upload page (Story 6.9). Reached via the emailed
 * magic link `/certificate-upload/:token` — anonymous, OUTSIDE any app shell (no chrome
 * that implies a session). It resolves the request context, lets the Vendor attach one
 * PDF/PNG/JPEG (optionally an approving authority + expiry), and submits to the public
 * POST. On success the document becomes a `pending` declaration in the admin
 * verification queue ("submitted for review"). An invalid/expired/used link shows a
 * single uniform state; a bad/infected file shows an inline error and stays usable.
 */
export function CertificateUpload({ token }: { token: string }): React.JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [approvingAuthority, setApprovingAuthority] = useState('');
  const [expiresAt, setExpiresAt] = useState(''); // an <input type="date"> value (YYYY-MM-DD)
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const context = await getCertificateUploadContext(token);
        if (!cancelled) setLoad({ kind: 'ready', context });
      } catch {
        // Uniform refusal — invalid, expired, or already used are indistinguishable.
        if (!cancelled) setLoad({ kind: 'invalid' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Attach a document (PDF, PNG, or JPEG).');
      return;
    }
    setSubmitting(true);
    try {
      await uploadCertificateForToken(token, file, {
        ...(approvingAuthority.trim() ? { approvingAuthority: approvingAuthority.trim() } : {}),
        // A date input yields YYYY-MM-DD; the API wants a full ISO timestamp WITH a tz
        // offset, so pin it to UTC midnight (matches Declare/CreateCertificateForm).
        ...(expiresAt ? { expiresAt: `${expiresAt}T00:00:00.000Z` } : {}),
      });
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--app-bg)',
        padding: 'var(--gutter)',
      }}
    >
      <section
        className="card stack"
        aria-labelledby="upload-heading"
        style={{ width: 'min(520px, 100%)' }}
      >
        {load.kind === 'loading' && (
          <p role="status" aria-live="polite" className="muted" style={{ margin: 0 }}>
            Loading…
          </p>
        )}

        {load.kind === 'invalid' && (
          <div className="stack" style={{ gap: 6 }}>
            <h1 id="upload-heading" style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
              This link can’t be used
            </h1>
            <p role="alert" className="muted" style={{ margin: 0, fontSize: 14 }}>
              This upload link is invalid, expired, or has already been used. If you still need
              to send a document, please ask the company to send you a new link.
            </p>
          </div>
        )}

        {load.kind === 'ready' && submitted && (
          <div className="stack" style={{ gap: 6 }} role="status" aria-live="polite">
            <h1 id="upload-heading" style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
              Submitted for review
            </h1>
            <p className="muted" style={{ margin: 0, fontSize: 14 }}>
              Thank you — your document has been submitted to {load.context.companyName} for review.
              You can close this page.
            </p>
          </div>
        )}

        {load.kind === 'ready' && !submitted && (
          <>
            <div className="stack" style={{ gap: 4 }}>
              <h1 id="upload-heading" style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
                Upload a certificate
              </h1>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                {load.context.companyName} has requested a certificate. No account is needed —
                just attach the document below.
              </p>
            </div>

            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
              <dt className="muted">Requested by</dt>
              <dd style={{ margin: 0 }}>{load.context.companyName}</dd>
              <dt className="muted">Framework</dt>
              <dd style={{ margin: 0 }}>
                {load.context.frameworkCode} — {load.context.frameworkName}
              </dd>
              <dt className="muted">{load.context.subjectType === 'device' ? 'Device' : 'Vendor'}</dt>
              <dd style={{ margin: 0 }}>{load.context.subjectLabel}</dd>
              <dt className="muted">Accepted files</dt>
              <dd style={{ margin: 0 }}>PDF, PNG, or JPEG</dd>
            </dl>

            <form className="stack" onSubmit={onSubmit} noValidate style={{ gap: 10 }}>
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Document</span>
                <input
                  type="file"
                  aria-label="Document"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    setError(null);
                  }}
                  style={fieldStyle}
                />
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Approving authority (optional)</span>
                <input
                  aria-label="Approving authority"
                  value={approvingAuthority}
                  onChange={(e) => setApprovingAuthority(e.target.value)}
                  maxLength={MAX_TEXT_LEN}
                  style={fieldStyle}
                />
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Expiry date (optional)</span>
                <input
                  type="date"
                  aria-label="Expiry date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  style={fieldStyle}
                />
              </label>

              <div role="alert" aria-live="assertive" style={{ minHeight: 18 }}>
                {error && (
                  <span className="status-pill" data-tone="bad">
                    <span className="status-pill__dot" aria-hidden="true" />
                    {error}
                  </span>
                )}
              </div>

              <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit for review'}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
